import { ContactParams } from "app/models/contact-params";
import { DealParams } from "app/models/deal-params";
import { EntityParams } from "app/models/entity-params";
import { DraftFacilityParams, IroValues } from "app/models/facility-params";
import { HolidayCalendarParams } from "app/models/holiday-calendar-params";
import { IndexRateOptionParams } from "app/models/index-rate-options-params";
import cleanUUID from "app/views/components/functions/cleanUUID";
import moment from "moment";
import { pikOptions, Option, rateSettingInstructions, roundingOptions, dayBasisOptions, frequencyOptions, rateTypeOptions } from "app/models/dropdown-options";
import { removeExistingActives } from "app/views/components/functions/removeExistingActives";


type CSVRow = Record<string, string>;


export function transformFacilityCSVToSubmission(
    csvRows: CSVRow[],
    deals: DealParams[],
    facilities: DraftFacilityParams[],
    indexRateOptions: IndexRateOptionParams[],
    entities: EntityParams[],
    contacts: ContactParams[],
    holidayCalendars: HolidayCalendarParams[],
    businessDate: Date,
    transformValues: (values: any) => any
): any[] {


    function findEntityId(entityName: string) {
        return entities.find(
            entity => entity.entityName === entityName
        )?.id
    }
    function findHolidayId(holidayName: string) {
        return holidayCalendars.find(
            hc => hc.name === holidayName
        )?.id
    }
    function findContactsId(contact: string) {
        return contacts.find(
            hc => hc.name === contact
        )?.id
    }
    function findIROKey(iro: string) {
        const indexOption = indexRateOptions.find(
            op => op.indexOption === iro
        )
        return { admin: indexOption?.public ?? '', id: indexOption?.id ?? '' }
    }
    function findDealKey(dealSystemId: string) {
        const deal = deals.find(
            op => op.systemId === dealSystemId
        )
        return { admin: deal?.accountManagementAdmin ?? '', id: deal?.id ?? '' }
    }
    const getMappedValue = (options: Option[], label: string): string => {
        const match = options.find((option) => option.label === label);
        return match ? match.value : '';
    };

    const processedRows = removeExistingActives(csvRows, facilities);

    return processedRows.map((row) => {
        const contractPeriod = [{
            contractPeriod: row['Contract Period'] ? getMappedValue(rateTypeOptions as Option[], row['Contract Period']) : '',
            cas: Number(row['CAS']) ?? 0
        }]
        const iroValues: IroValues[] = [{
            indexOption: row['IRO Index Rate Option'] ? findIROKey(row['IRO Index Rate Option']) : { admin: '', id: '' },
            margin: Number(row['IRO Margin']) ?? 0,
            dayBasis: row['IRO Day Basis'] ? getMappedValue(dayBasisOptions, row['IRO Day Basis']) : '',
            rounding: row['IRO Rounding'] ? getMappedValue(roundingOptions, row['IRO Rounding']) : '',
            currency: row['IRO Currency'] ?? '',
            contractPeriod: row['Contract Period'] ? contractPeriod : [],
            rateInstruction: row['IRO Rate Instruction'] ? getMappedValue(rateSettingInstructions, row['IRO Rate Instruction']) : null,
            interestPaymentFrequency: row['IRO Frequency'] ? getMappedValue(frequencyOptions, row['IRO Frequency']) : null,
            interestFirstPaymentDate: row['IRO First Payment Date'] ? moment(row['IRO First Payment Date']).format('YYYY-MM-DD').toUpperCase() : null,
            fixedRate: row['IRO Fixed Rate'] ?? null,
            iroFloor: Number(row['IRO Floor']) ?? false,
            iroCap: Number(row['IRO Cap']) ?? false,
            indexType: row['IRO Index Type'] ?? null
        }]
        const nameFromCSV = row['Facility Name']?.trim()
        const isNameAutoGenerated = !nameFromCSV
        const updatedRow = {
            id: facilities.find(facility => facility?.clientId === row['Client ID'])?.id || null,
            name: isNameAutoGenerated ? (() => {
                const amountToUse = row['Original Amount'] && Number(row['Original Amount']) > 0
                    ? row['Original Amount']
                    : row['Current Amount'];
                const formattedAmount =
                    Number(amountToUse) > 0
                        ? `${new Intl.NumberFormat('en-US', {
                            style: 'decimal',
                            maximumFractionDigits: 1,
                            minimumFractionDigits: 1,
                            notation: 'compact',
                        }).format(Number(amountToUse))} ${row['Base Currency'] ?? 'USD'}`
                        : '';
                return `${row['Facility Type'] ?? ''} (${moment(row['Start Date']).format('YYYY-MMM').toUpperCase()}) ${formattedAmount}`
            })() : nameFromCSV,
            isNameAutoGenerated: isNameAutoGenerated,
            dealId: findDealKey(row['Deal ID'] ?? '') ?? '',
            amount: row['Current Amount'] ?? 0,
            originalAmount: row['Original Amount'] ?? undefined,
            utilizedAmount: row['Utilized Amount'] ?? 0,
            amortizationScheduleId: row['Amortization Schedule ID'] ?? null,
            accrualFee: [],
            holidayCalendar: row["Holiday Calendars"] ? row["Holiday Calendars"].split("|").map(findHolidayId) : [],
            adminEntity: row["Administrative Agent"] ? findEntityId(row["Administrative Agent"]) : '',
            opsAdmin: row["Administrative Agent Contact"] ? cleanUUID(findContactsId(row["Administrative Agent Contact"])) : '',
            currencies: row['Available Currencies'] ? Array.from(new Set([...row["Available Currencies"].split("|"), row['Base Currency']])) : [row['Base Currency']],
            baseCurrency: row['Base Currency'] ?? 'USD',
            borrowers: row["Borrowers"] ? row["Borrowers"].split("|").map(findEntityId) : [],
            isSublimit: row['Sublimits'] === 'Yes' ? 'true' : 'false',
            lenderEligibilityCriteria: row['Lender Eligibility Criteria'] ? row['Lender Eligibility Criteria'].split("|") : [],
            facilityType: row['Facility Type'] ?? '',
            pikOption: row['PIK Options'] ? getMappedValue(pikOptions, row['PIK Options']) : 'No_PIK',
            startDate: row['Start Date'] ?? businessDate,
            maturity: row['Maturity Date'] ?? businessDate,
            cusip: row['CUSIP'] ?? null,
            isin: row['ISIN'] ?? null,
            lxId: row['LXID'] ?? null,
            bbgId: row['BBG ID'] ?? null,
            clientId: row['Client ID'] ?? null,
            borrowerPaymentInstructions: row["Borrower Payment Instructions"] ? row["Borrower Payment Instructions"].split("|") : [],
            adminAgentPaymentInstructions: row["Admin Agent Payment Instructions"] ? row["Admin Agent Payment Instructions"].split("|") : [],
            subLimits: [], //we may add this later, but we are only doing iro for now
            iroValues: row['IRO Index Rate Option'] ? iroValues : [],
            restrictLoanDate: row['Restrict Loan Date'] ?? false,
            nextAmortizationPaymentData: row['Next Amortization Payment Data'] ?? null,
            pikScheduler: row['Pik Scheduler'] ?? null,
            classification: row['Classification'] ?? null,
            systemId: row["System ID"] || null,
            facilityPricingGrid: [],
        };
        const tranformedDeals = transformValues(updatedRow);
        return tranformedDeals;
    });
}
